import { PLACE_ID_PATTERN } from '@bandido/shared/types';
import type {
  ConnectorInfo,
  PassPassengerPayload,
  RailPassengerPayload,
  RailSearchPayload,
} from './queries';
import {
  SearchPassengerDraft,
  railPassengerRules,
  validateSearchPassengers,
} from './searchPassengers';

export type RailPassengerDraft = SearchPassengerDraft;

export type RailPassengerAgeError = 'required' | 'invalid' | 'unsupported' | undefined;

export const DEFAULT_RAIL_PASSENGER: RailPassengerDraft = { type: 'adult', age: '' };

function configuredRailConnectors(connectors: readonly ConnectorInfo[] | undefined): ConnectorInfo[] {
  return (connectors ?? []).filter((connector) => connector.vertical === 'rail' && connector.configured);
}

/** El array sólo es seguro si todos los conectores del fan-out lo aceptan. */
export function railTypedPassengersEnabled(connectors: readonly ConnectorInfo[] | undefined): boolean {
  const configured = configuredRailConnectors(connectors);
  if (!configured.length || !configured.every((connector) => connector.passengerTypes?.enabled === true)) return false;
  return configured[0].passengerTypes!.supported.some((type) =>
    configured
      .slice(1)
      .every((connector) => connector.passengerTypes!.supported.includes(type)));
}

/** Tipos aceptados por todos los conectores Rail que participan del fan-out. */
export function railSupportedPassengerTypes(
  connectors: readonly ConnectorInfo[] | undefined,
): RailPassengerPayload['type'][] {
  const configured = configuredRailConnectors(connectors);
  if (!configured.length || !railTypedPassengersEnabled(configured)) return [];
  const [first, ...rest] = configured;
  return first.passengerTypes!.supported.filter((type) =>
    rest.every((connector) => connector.passengerTypes!.supported.includes(type)));
}

/** Una sola autoridad para error de campo, bloqueo y payload. */
export function railPassengerFromDraft(
  draft: RailPassengerDraft,
  supportedTypes?: readonly RailPassengerPayload['type'][],
): {
  error: RailPassengerAgeError;
  passenger?: RailPassengerPayload;
} {
  const acceptedTypes: readonly RailPassengerPayload['type'][] =
    supportedTypes ?? ['adult', 'youth', 'senior'];
  const [error] = validateSearchPassengers(
    [draft],
    railPassengerRules(acceptedTypes, true),
  ).errors;
  if (error) return { error };
  const railType = draft.type as RailPassengerPayload['type'];
  const ageText = draft.age.trim();
  return {
    error: undefined,
    passenger: {
      type: railType,
      ...(ageText ? { age: Number(ageText) } : {}),
    },
  };
}

export function railPassengersFromDraft(
  drafts: readonly RailPassengerDraft[],
  supportedTypes?: readonly RailPassengerPayload['type'][],
): RailPassengerPayload[] | null {
  const derived = drafts.map((draft) => railPassengerFromDraft(draft, supportedTypes));
  if (!derived.length || derived.length > 9 || derived.some((entry) => !entry.passenger)) return null;
  return derived.map((entry) => entry.passenger!);
}

/** `adult` preserva edad opcional; youth/senior siempre salen con edad. */
export function railPassengersToParam(passengers: readonly RailPassengerPayload[]): string {
  return passengers.map(({ type, age }) => (age === undefined ? type : `${type}:${age}`)).join(',');
}

export function railDraftsToParam(drafts: readonly RailPassengerDraft[]): string {
  return drafts.map(({ type, age }) => age.trim() ? `${type}:${age}` : type).join(',');
}

/** Parser estricto: entradas inválidas o más de nueve fallan, nunca se truncan. */
export function railPassengersFromParam(raw: string | undefined): RailPassengerPayload[] | null {
  if (!raw) return null;
  const entries = raw.split(',');
  if (!entries.length || entries.length > 9) return null;
  const passengers: RailPassengerPayload[] = [];
  for (const entry of entries) {
    const [type, age, extra] = entry.split(':');
    if (extra !== undefined || !['adult', 'youth', 'senior'].includes(type)) return null;
    const derived = railPassengerFromDraft({ type: type as RailPassengerPayload['type'], age: age ?? '' });
    if (!derived.passenger) return null;
    passengers.push(derived.passenger);
  }
  return passengers;
}

export function railDraftsFromParam(raw: string | undefined): RailPassengerDraft[] {
  if (/^[1-9]$/.test(raw ?? '')) {
    return Array.from({ length: Number(raw) }, () => ({ ...DEFAULT_RAIL_PASSENGER }));
  }
  return (
    railPassengersFromParam(raw)?.map((passenger) => ({
      type: passenger.type,
      age: passenger.age === undefined ? '' : String(passenger.age),
    })) ?? [{ ...DEFAULT_RAIL_PASSENGER }]
  );
}

export function railPassengerCount(passengers: number | readonly RailPassengerPayload[] | undefined): number {
  if (typeof passengers === 'number') return passengers;
  if (passengers === undefined) return 1;
  return passengers.length;
}

/** Conserva pax=N al hidratar capabilities mientras la composición siga intacta. */
export function railDraftParam(
  committedParam: string | undefined,
  drafts: readonly RailPassengerDraft[],
  edited: boolean,
): string {
  if (
    !edited &&
    /^[1-9]$/.test(committedParam ?? '') &&
    Number(committedParam) === drafts.length
  ) {
    return committedParam!;
  }
  return railDraftsToParam(drafts);
}

/** Cross-sell conserva la composición tipada; jamás inventa una edad faltante. */
export function railPassPassengers(
  passengers: number | readonly RailPassengerPayload[] | undefined,
  residence: string,
): PassPassengerPayload[] | null {
  if (typeof passengers === 'number' || passengers === undefined) {
    const passengerCount = passengers ?? 1;
    return Array.from({ length: passengerCount }, () => ({
      type: 'adult' as const,
      age: 30,
      nationality: residence,
      countryOfResidence: residence,
    }));
  }
  if (passengers.some((passenger) => passenger.age === undefined)) return null;
  return passengers.map((passenger) => ({
    type: passenger.type,
    age: passenger.age!,
    nationality: residence,
    countryOfResidence: residence,
  }));
}

/** Sólo expone la respuesta que pertenece al cross-sell elegible actualmente abierto. */
export function railPassResultsAreCurrent(
  resultIdentity: string | undefined,
  currentRequestIdentity: string | undefined,
): boolean {
  return Boolean(resultIdentity && currentRequestIdentity && resultIdentity === currentRequestIdentity);
}

/** Wiring puro URL → request: mantiene reejecutables ambos formatos. */
export function railSearchPayloadFromParams(params: Record<string, string>): RailSearchPayload | null {
  if (
    params.buscar !== '1' ||
    !params.origen ||
    !params.destino ||
    !params.fecha ||
    !PLACE_ID_PATTERN.test(params.origen) ||
    !PLACE_ID_PATTERN.test(params.destino)
  ) return null;
  const passengers = !params.pax
    ? 1
    : /^[1-9]$/.test(params.pax)
      ? Number(params.pax)
      : railPassengersFromParam(params.pax);
  if (!passengers) return null;
  return {
    fromStation: params.origen,
    toStation: params.destino,
    date: params.fecha,
    passengers,
  };
}

/** Nunca dispara un array tipado hasta contar con autoridad vigente. */
export function authorizedRailSearchPayloadFromParams(
  params: Record<string, string>,
  connectors: readonly ConnectorInfo[] | undefined,
): RailSearchPayload | null {
  const payload = railSearchPayloadFromParams(params);
  if (!payload || !Array.isArray(payload.passengers)) return payload;
  if (!railTypedPassengersEnabled(connectors)) return null;
  const supported = railSupportedPassengerTypes(connectors);
  return payload.passengers.every((passenger) => supported.includes(passenger.type))
    ? payload
    : null;
}
